import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import { aboutInfo, faqs } from "@/mocks/aboutData";

export default function AboutPage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const toggleFaq = useCallback((index: number) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  }, []);

  const handleContactSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      return;
    }
    const formData = new FormData(form);
    formData.delete("website_alt");
    try {
      const response = await fetch("https://readdy.ai/api/form/d9q541cnlsngrm4llrn0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormError("");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
        if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
          setFormStatus("error");
          setFormError("提交失败，请稍后重试");
        } else {
          setFormStatus("error");
          setFormError(serverMsg || "提交失败，请稍后重试");
        }
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查连接后重试");
    }
  }, []);

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于必赢国际",
    description: "必赢国际品牌介绍、信誉保障与联系方式",
    url: "https://www.biyingguoji.com/about",
    isPartOf: {
      "@type": "WebSite",
      name: "必赢国际",
      url: "https://www.biyingguoji.com",
    },
    about: {
      "@type": "Organization",
      name: "必赢国际",
      description: "必赢国际是备受信赖的国际化综合平台，提供安全、公正、多元的优质服务体验",
      foundingDate: "2015",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+65-6789-0000",
        contactType: "customer service",
        email: "service@biyingguoji.com",
        availableLanguage: ["Chinese", "English"],
        hoursAvailable: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          opens: "00:00",
          closes: "23:59",
        },
      },
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="pt-16">
      <SEOHead
        title="关于我们 - 必赢国际品牌介绍与信誉保障"
        description="了解必赢国际的品牌故事、核心价值、信誉认证与联系方式。必赢国际是备受信赖的国际化综合平台，致力于为全球用户提供安全、公正、多元的优质服务。"
        keywords="必赢国际关于我们,必赢国际品牌,必赢国际信誉,必赢国际联系方式"
        canonicalUrl="https://www.biyingguoji.com/about"
        ogTitle="关于必赢国际 - 品牌介绍与信誉保障"
        ogDescription="了解必赢国际的品牌故事、核心价值、信誉认证与联系方式。"
        structuredData={aboutSchema}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Breadcrumb */}
      <div className="bg-background-100 border-b border-background-200/70">
        <Breadcrumb items={[{ label: t("about.title") }]} />
      </div>

      {/* Brand Introduction */}
      <section className="w-full py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                {t("about.title")}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                {t("about.brand")}
              </h1>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                {aboutInfo.brand.content.map((paragraph, i) => (
                  <p key={i} className="text-base text-foreground-700 leading-relaxed mb-5">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="relative h-80 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20luxurious%20corporate%20headquarters%20interior%20with%20warm%20golden%20accents%2C%20elegant%20marble%20and%20glass%20architecture%2C%20soft%20ambient%20lighting%2C%20premium%20office%20lobby%20aesthetic%2C%20professional%20atmosphere%2C%20no%20people%20no%20text&width=800&height=640&seq=about-brand-img&orientation=portrait"
                  alt="必赢国际品牌形象"
                  title="必赢国际品牌形象"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reputation & Certifications */}
      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                {t("about.reputation")}
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                国际权威认证
              </h2>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-14">
              {aboutInfo.reputation.stats.map((stat, i) => (
                <div key={i} className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Certifications */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutInfo.reputation.certifications.map((cert, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="w-12 h-12 shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${cert.icon} text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                      {cert.name}
                    </h3>
                    <p className="text-sm text-foreground-600">{cert.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                常见问题
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                您可能关心的问题
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-100/70 transition-colors"
                  >
                    <span className="font-heading text-base font-semibold text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`text-lg text-foreground-500 shrink-0 transition-transform duration-300 ${
                        openFaq === index ? "ri-subtract-line" : "ri-add-line"
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                {t("about.contact")}
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                与我们取得联系
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div className="space-y-5">
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">办公地址</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.workingHours}</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                  发送消息
                </h3>
                <form onSubmit={handleContactSubmit} data-readdy-form="">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hidden-abs"
                  />
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-5 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                    {formStatus === "success" && (
                      <p className="text-sm text-accent-600 text-center">消息发送成功！我们会尽快与您联系。</p>
                    )}
                    {formStatus === "error" && (
                      <p className="text-sm text-primary-600 text-center">{formError || "发送失败，请稍后重试"}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}